import React from "react";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import GeneratedDocumentLink from "../GeneratedDocumentLink";
import { Answer } from "../Chat8PerguntasPanel.types";

interface Chat8PerguntasPanelQuestionsListProps {
  questions: string[];
  answers: Answer;
  intro: string;
  outro: string;
  freeFormMessage: string;
  isSubmitting: boolean;
  firstAnswerRef: React.RefObject<HTMLTextAreaElement>;
  onAnswerChange: (questionIndex: number, value: string) => void;
  onFreeFormMessageChange: (value: string) => void;
}

export const Chat8PerguntasPanelQuestionsList: React.FC<Chat8PerguntasPanelQuestionsListProps> = React.memo(
  ({
    questions,
    answers,
    intro,
    outro,
    freeFormMessage,
    isSubmitting,
    firstAnswerRef,
    onAnswerChange,
    onFreeFormMessageChange,
  }) => {
    // Function to render introduction with download links
    const renderIntroWithLinks = (text: string) => {
      if (!text) return null;

      const placeholderRegex = /\[#!DOWNLOAD_LINK=([^=]+)=([^#\]]+)#!\]/g;
      const parts = text.split(placeholderRegex);

      return (
        <p className="text-sm text-platinum/80 whitespace-pre-wrap leading-relaxed">
          {parts.map((part, index) => {
            if (index % 3 === 1) { // Document ID
              const docId = part;
              const docName = parts[index + 1];
              return (
                <GeneratedDocumentLink
                  key={docId}
                  generatedDocId={docId}
                  documentName={docName}
                  className="inline-block mx-1"
                />
              );
            }
            if (index % 3 === 2) { // Document name, already consumed
              return null;
            }
            return part; // Normal text part
          })}
        </p>
      );
    };

    return (
      <div className="space-y-6">
        {intro && (
          <div className="glass-card p-4 rounded-lg border border-white/10">
            <Badge
              variant="secondary"
              className="mb-3 bg-blue-500/20 text-blue-300 border-blue-500/30"
              aria-label="Contexto da mensagem do assistente"
            >
              Contexto
            </Badge>
            {renderIntroWithLinks(intro)}
          </div>
        )}

        {questions.map((question, index) => (
          <div key={index} className="glass-card p-4 rounded-lg border border-white/10 space-y-3 hover:border-white/20 transition-colors">
            <div className="flex items-start gap-3">
              <Badge
                variant="outline"
                className="shrink-0 bg-blue-500/20 text-blue-300 border-blue-500/30 font-semibold"
              >
                {index + 1}
              </Badge>
              <label
                htmlFor={`question-${index}`}
                className="flex-1 text-sm font-medium text-platinum leading-relaxed"
              >
                {question}
              </label>
            </div>
            <Textarea
              id={`question-${index}`}
              ref={index === 0 ? firstAnswerRef : null}
              value={answers[index] || ""}
              onChange={(e) => onAnswerChange(index, e.target.value)}
              placeholder="Digite sua resposta aqui..."
              className="w-full min-h-[100px] bg-navy-700/50 border-white/10 text-platinum placeholder:text-silver/50 focus:border-blue-500/50 focus:ring-blue-500/20 resize-none"
              rows={4}
              disabled={isSubmitting}
            />
          </div>
        ))}

        <Separator className="bg-white/10" />

        <div className="glass-card p-4 rounded-lg border border-white/10 space-y-3">
          <label
            htmlFor="free-form-message"
            className="flex items-center gap-2 text-sm font-medium text-platinum"
          >
            <Badge variant="outline" className="bg-purple-500/20 text-purple-300 border-purple-500/30">
              Opcional
            </Badge>
            Comentário Adicional
          </label>
          <Textarea
            id="free-form-message"
            value={freeFormMessage}
            onChange={(e) => onFreeFormMessageChange(e.target.value)}
            placeholder="Se precisar, adicione um comentário ou pergunta livre aqui..."
            className="w-full min-h-[80px] bg-navy-700/50 border-white/10 text-platinum placeholder:text-silver/50 focus:border-blue-500/50 focus:ring-blue-500/20 resize-none"
            rows={3}
            disabled={isSubmitting}
          />
        </div>

        {outro && (
          <div className="glass-card p-4 rounded-lg border border-white/10">
            <Badge variant="secondary" className="mb-3 bg-amber-500/20 text-amber-300 border-amber-500/30">
              Instruções
            </Badge>
            <p className="text-sm text-platinum/80 whitespace-pre-wrap leading-relaxed">
              {outro}
            </p>
          </div>
        )}
      </div>
    );
  }
);

Chat8PerguntasPanelQuestionsList.displayName = "Chat8PerguntasPanelQuestionsList"; 